import React, { useCallback, useEffect, useState } from "react";
import { Handshake, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError, useAuth } from "@/lib/auth";
import { inputCls } from "./ui";
import { Row } from "./ProfileTab";

const pname = (p) => `${p.first_name || ""} ${p.last_name || ""}`.trim() + (p.organization ? ` · ${p.organization}` : "");
const kind = (t) => (t || "").replace(/_/g, " ");

/** Profile card: the resident's current partners, with Assign / End for people allowed to. */
export function PartnersRow({ resident }) {
  const { user } = useAuth();
  const staff = user && user.role !== "partner_user" && user.role !== "resident";
  const [d, setD] = useState(null);
  const [pick, setPick] = useState("");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const url = `/par/resident-partners/${resident.id}`;
  const load = useCallback(() => { if (staff) apiClient.get(url).then(({ data }) => setD(data)).catch(() => setD(null)); }, [staff, url]);
  useEffect(() => { load(); }, [load]);
  if (!staff || !d) return null;
  const run = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); setD(data); toast.success(ok); window.dispatchEvent(new Event("par:assignments-changed")); return true; }
    catch (e) { toast.error(formatApiError(e)); return false; } finally { setBusy(false); }
  };
  const assign = () => run(() => apiClient.post(url, { partner_id: pick }), "Partner assigned").then((ok) => { if (ok) { setPick(""); setAdding(false); } });
  const end = (a) => window.confirm(`End ${a.partner_name || "this partner"}'s assignment to ${resident.first_name}?`) && run(() => apiClient.post(`${url}/${a.id}/end`), "Assignment ended");
  return (
    <div className="sm:col-span-2"><Row label="Partners" testId="profile-partners">
      <div className="space-y-2">
        {!d.assignments.length && <div className="text-nex-muted" data-testid="profile-partners-empty">No partners assigned</div>}
        {d.assignments.map((a) => (
          <div key={a.id} data-testid={`profile-partner-${a.id}`} className="flex items-center gap-2">
            <Handshake className="h-3.5 w-3.5 text-nex-gold shrink-0" />
            <span className="truncate">{a.partner_name || a.partner_id}</span>
            {a.partner_type && <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-nex-muted">{kind(a.partner_type)}</span>}
            {d.can_assign && <button type="button" disabled={busy} onClick={() => end(a)} data-testid={`profile-partner-end-${a.id}`}
              className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] uppercase text-nex-muted hover:text-red-300 disabled:opacity-40"><X className="h-3 w-3" /> End</button>}
          </div>
        ))}
        {d.can_assign && !["archived", "exited"].includes(resident.status) && (adding ? (
          <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="profile-partner-assign-form">
            <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="profile-partner-select" className={`${inputCls} !py-1 flex-1 min-w-[180px]`}>
              <option value="">Pick a partner…</option>
              {(d.partners || []).map((p) => <option key={p.id} value={p.id}>{pname(p)} · {kind(p.type)}{p.status === "pending" ? " (pending)" : ""}</option>)}
            </select>
            <button type="button" disabled={!pick || busy} onClick={assign} data-testid="profile-partner-assign-save" className="font-mono text-[10px] uppercase text-nex-gold disabled:opacity-40">Assign</button>
            <button type="button" onClick={() => { setAdding(false); setPick(""); }} data-testid="profile-partner-assign-cancel" className="font-mono text-[10px] uppercase text-nex-muted">Cancel</button>
          </div>
        ) : <button type="button" onClick={() => setAdding(true)} data-testid="profile-partner-assign-open" className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-nex-gold"><Plus className="h-3 w-3" /> Assign to partner</button>)}
      </div>
    </Row></div>
  );
}
